import { FC, useEffect, useState } from 'react';

import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { Box } from '@zoopla-eng/zds-components/box';
import { ValidatedMedium } from '@zoopla-eng/zds-components/icons';
import { Alert } from '@zoopla-eng/zds-components/alert';
import { MojoBanner, type MojoBannerTrackingValues } from '@zoopla/mojo-banner';
import { BaseTaxonomy } from '@zoopla/tracking';

import { TrackProperty } from '../../components';
import { TrackedProperty, getTrackedProperty } from '../../helpers/property';
import {
    headingStyle,
    branchesStyle,
    branchStyle,
    estimateWrapperStyle,
    successPageWrapper,
    widgetWrapper,
} from './Success.styles.css';
import InstantValuation from '../InstantValuation/InstantValuation';

export type SuccessBranch = {
    branchDisplayName?: string | null;
    logo?: string | null;
    branchId: number;
};

type WithMojoBannerProps = {
    showMojoBanner: true;
    mojoBannerTrackingValues: MojoBannerTrackingValues;
};

type WithoutMojoBannerProps = {
    showMojoBanner?: false;
    mojoBannerTrackingValues?: never;
};

export type SuccessProps = {
    branches: SuccessBranch[];
    uprn: string;
    fullAddress: string;
    postcode: string;
    baseTaxonomy: BaseTaxonomy;
} & (WithMojoBannerProps | WithoutMojoBannerProps);

export const Success: FC<SuccessProps> = ({
    branches,
    uprn,
    fullAddress,
    postcode,
    baseTaxonomy,
    ...props
}) => {
    const [property, setProperty] = useState<TrackedProperty | null>(null);
    // This is set to true as the first time the user sees this success page,
    // we want to success banner to be rendered.
    const [hasSuccessBanner, setHasSuccessBanner] = useState(true);

    useEffect(() => {
        const abortController = new AbortController();

        async function fetchPropertyAsync() {
            if (!uprn) return;
            const property = await getTrackedProperty(uprn, {
                signal: abortController.signal,
            });
            setProperty(property);
        }

        fetchPropertyAsync();

        return () => {
            abortController.abort();
        };
    }, [uprn]);

    return (
        <>
            {hasSuccessBanner && (
                <Box pt="S400">
                    <Alert
                        primaryMessage="Your enquiry has been sent to your chosen agents."
                        supportingMessage="They’ll give you a call soon to make arrangements for a valuation visit."
                        level="success"
                        isFullWidth={true}
                        onDismiss={() => {
                            setHasSuccessBanner(false);
                        }}
                    />
                </Box>
            )}

            <div className={successPageWrapper}>
                <div>
                    <h1 className={`${headingStyle} ${fontGroups.kettle}`}>
                        Enquiry sent to:
                    </h1>
                    {!!branches.length && (
                        <div>
                            <ul
                                className={branchesStyle}
                                aria-label="Chosen agents"
                            >
                                {branches.map((branch) => (
                                    <li
                                        className={`${branchStyle} ${fontGroups.ladle}`}
                                        key={branch.branchId}
                                        aria-label={
                                            branch.branchDisplayName || ''
                                        }
                                    >
                                        <ValidatedMedium accessibilityType="decorative" />
                                        {branch.branchDisplayName}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className={widgetWrapper}>
                    <div className={estimateWrapperStyle}>
                        <InstantValuation
                            fullAddress={fullAddress}
                            postcode={postcode}
                            saleEstimate={property?.saleEstimate}
                            isLoading={!property}
                        />
                    </div>
                    {props.showMojoBanner && (
                        <Box pb="S500">
                            <MojoBanner
                                variant="success"
                                trackingValues={props.mojoBannerTrackingValues}
                            />
                        </Box>
                    )}

                    <TrackProperty
                        uprn={uprn}
                        fullAddress={fullAddress}
                        baseTaxonomy={baseTaxonomy}
                    />
                </div>
            </div>
        </>
    );
};

export default Success;
